import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { patchNameFormFromPersonSimpleWithRole } from '@app/components/name-form/name-form.helper';
import { PersonContactCardFormGroup } from '@app/components/person-contact-card/person-contact-form/person-contact-card-form.helper';
import { TitleNameFormGroup } from '@app/components/title-name-form/title-name-form.helper';
import { resolveNameTitleCodeForSelect } from '@app/helpers/name-title-select.helper';
import { FileUploadControl } from '@app/models/form.models';
import { LocalizedValue } from '@app/models/shared.models';
import {
  PersonProjectRequest,
  PersonRequest,
} from '@app/models/project-setup.models';
import { PersonSimpleWithRole } from '@app/models/project.models';
import { FileType } from '@app/models/shared.enums';
import { FileData } from '@app/services/file/upload.service';
import { v4 as uuidv4 } from 'uuid';

export function isFormValid(formGroup: FormGroup): boolean {
  formGroup.markAllAsTouched();
  formGroup.updateValueAndValidity();
  return formGroup.status === 'VALID';
}

export function isBooleanLikeTrue(
  value: boolean | string | null | undefined,
): boolean {
  return value === true || value === 'true';
}

export function getNewFileFormGroup(
  fb: FormBuilder,
  fileType: FileType | null = null,
  isRequired: boolean = false,
  defaultIsPublic: boolean = false,
): FormGroup<FileUploadControl> {
  return fb.nonNullable.group({
    fileType: fb.control<FileType | null>(fileType),
    id: fb.nonNullable.control<string>(uuidv4()),
    isPublic: fb.nonNullable.control<boolean>(defaultIsPublic),
    description: fb.control<string | null>(null),
    title: fb.control<string | null>(
      null,
      isRequired ? Validators.required : null,
    ),
    file: fb.control<File | null>(
      null,
      isRequired ? Validators.required : null,
    ),
  });
}

const personData: PersonRequest = {
  titleCode: '',
  firstName: '',
  midName: '',
  lastName: '',
  citizenship: null,
  birthPlace: null,
  birthDate: null,
  email: null,
  position: null,
  motherFirstName: null,
  motherMidName: null,
  motherLastName: null,
  maidenFirstName: null,
  maidenMidName: null,
  maidenLastName: null,
  idCardType: 1, // TODO is this mandatory? 1 = PersonalIdCard
  idCardNumber: null,
  idCardNumberExpiry: null,
  taxIdentifier: null,
  bankAccountNumber: null,
  address: null,
};

export function getTitleNameFormDataAsPersonProject(
  titleNameForm: FormGroup<TitleNameFormGroup>,
): PersonProjectRequest {
  return {
    connectionTypeCode:
      titleNameForm.controls.name.controls.connectionTypeCode.value,
    description: null,
    person: {
      ...personData,
      titleCode: titleNameForm.controls.title.value,
      firstName: titleNameForm.controls.name.controls.firstName.value!,
      midName: titleNameForm.controls.name.controls.middleName.value,
      lastName: titleNameForm.controls.name.controls.lastName.value!,
    },
  };
}

export function getPersonContactCardFormGroupFormDataAsPersonProject(
  personContactCardForm: FormGroup<PersonContactCardFormGroup>,
): PersonProjectRequest {
  return {
    connectionTypeCode:
      personContactCardForm.controls.nameTitle.controls.name.controls
        .connectionTypeCode.value,
    description: null,
    person: {
      ...personData,
      titleCode: personContactCardForm.controls.nameTitle.controls.title.value,
      firstName:
        personContactCardForm.controls.nameTitle.controls.name.controls
          .firstName.value!,
      midName:
        personContactCardForm.controls.nameTitle.controls.name.controls
          .middleName.value,
      lastName:
        personContactCardForm.controls.nameTitle.controls.name.controls.lastName
          .value!,
      email:
        personContactCardForm.controls.emailPosition.controls.emailAddress
          .value,
      position:
        personContactCardForm.controls.emailPosition.controls.position.value,
    },
  };
}

export function getPersonContactCardFormGroupFormArrayDataAsPersonProjectArray(
  personContactCardForm: FormArray<FormGroup<PersonContactCardFormGroup>>,
): PersonProjectRequest[] {
  const tempPersons: PersonProjectRequest[] = [];
  personContactCardForm.controls.forEach(
    (titleNameForm: FormGroup<PersonContactCardFormGroup>) => {
      tempPersons.push(
        getPersonContactCardFormGroupFormDataAsPersonProject(titleNameForm),
      );
    },
  );

  return tempPersons;
}

export function patchTitleNameForm(
  formGroup: FormGroup<TitleNameFormGroup>,
  patchData: PersonSimpleWithRole,
  nameTitles?: readonly LocalizedValue[],
): void {
  const raw = patchData.titleCode;
  formGroup.controls.title.setValue(
    nameTitles?.length
      ? resolveNameTitleCodeForSelect(raw, nameTitles)
      : (raw ?? null),
  );
  patchNameFormFromPersonSimpleWithRole(formGroup.controls.name, patchData);
}

export function getFileDataFormFileUploadForm(
  formGroup: FormGroup<FileUploadControl>,
): FileData | null {
  const file = formGroup.controls.file.value;
  const fileType = formGroup.controls.fileType.value;
  if (file === null || fileType === null) {
    return null;
  }
  const fromDescription = formGroup.controls.description.value?.trim() ?? '';
  const fromTitle = formGroup.controls.title.value?.trim() ?? '';
  const uploadedFileDescription = fromDescription || fromTitle || file.name;
  return {
    uploadedFile: file,
    uploadedFileDescription,
    uploadedFileIsPublic: formGroup.controls.isPublic.value,
    uploadedFileType: fileType,
  };
}

export function fileFormArrayToFormDataArray(
  fileFormArray: FormArray<FormGroup<FileUploadControl>>,
): FileData[] {
  const fileData: FileData[] = [];
  fileFormArray.controls.forEach((formGroup) => {
    const fileDataTemp = getFileDataFormFileUploadForm(formGroup);
    if (fileDataTemp !== null) {
      fileData.push(fileDataTemp);
    }
  });
  return fileData;
}
